import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { ASSETS } from "../assetManifest.js";
import { CONSTANTS } from "../data";

interface TutorialOverlayProps {
  tutorialState: any;
  onUpdateState: (newState: any) => void;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  tutorialState,
  onUpdateState,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const { isTutorialActive, currentTutorial } = tutorialState;
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalStyle, setModalStyle] = useState<React.CSSProperties>({});

  const tutorialDef = useMemo(() => {
    if (!currentTutorial) return null;
    return CONSTANTS.TUTORIALS[currentTutorial.id] || null;
  }, [currentTutorial]);

  const currentStepInfo = useMemo(() => {
    if (!tutorialDef || currentTutorial === null) return null;
    return tutorialDef.steps[currentTutorial.step];
  }, [tutorialDef, currentTutorial]);

  useEffect(() => {
    if (!isTutorialActive || !currentStepInfo?.highlightQuery) {
      setTargetRect(null);
      return;
    }
    const updateRect = () => {
      const element = document.querySelector(currentStepInfo.highlightQuery!);
      if (element) {
        setTargetRect(element.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    };
    updateRect();
    const interval = setInterval(updateRect, 100);
    window.addEventListener("resize", updateRect);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", updateRect);
    };
  }, [isTutorialActive, currentStepInfo]);

  useEffect(() => {
    if (!isTutorialActive) return;

    const defaultStyle: React.CSSProperties = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };

    if (!targetRect || !modalRef.current) {
      setModalStyle(defaultStyle);
      return;
    }

    const modalW = modalRef.current.offsetWidth;
    const modalH = modalRef.current.offsetHeight;
    const margin = window.innerWidth < 640 ? 10 : 20; // Smaller margin on mobile

    let top = targetRect.bottom + margin;
    let left = targetRect.left + targetRect.width / 2 - modalW / 2;

    if (top + modalH > window.innerHeight - margin) {
      top = targetRect.top - modalH - margin;
    }
    if (top < margin) {
      top = margin;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - modalW - margin));

    setModalStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      transform: "none",
    });
  }, [isTutorialActive, targetRect]);

  if (!isTutorialActive || !currentTutorial || !tutorialDef || !currentStepInfo)
    return null;

  const advanceStep = () => {
    if (currentTutorial.step < tutorialDef.steps.length - 1) {
      onUpdateState({
        tutorialState: {
          ...tutorialState,
          currentTutorial: {
            ...currentTutorial,
            step: currentTutorial.step + 1,
          },
        },
      });
    } else {
      const newCompleted = [...tutorialState.completedTutorials];
      if (!newCompleted.includes(currentTutorial.id)) {
        newCompleted.push(currentTutorial.id);
      }
      onUpdateState({
        tutorialState: {
          ...tutorialState,
          isTutorialActive: false,
          currentTutorial: null,
          completedTutorials: newCompleted,
        },
      });
    }
  };

  const skipTutorial = () => {
    const newSkipped = [...tutorialState.skippedTutorials];
    if (!newSkipped.includes(currentTutorial.id)) {
      newSkipped.push(currentTutorial.id);
    }
    onUpdateState({
      tutorialState: {
        ...tutorialState,
        isTutorialActive: false,
        currentTutorial: null,
        skippedTutorials: newSkipped,
      },
    });
  };

  const isInteractive = !!currentStepInfo.action;
  const isLastStep = currentTutorial.step === tutorialDef.steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {targetRect && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute pointer-events-none"
          style={{
            left: targetRect.left - 8,
            top: targetRect.top - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
        >
          <div className="w-full h-full border-4 border-yellow-400 rounded-lg shadow-2xl animate-pulse bg-yellow-400 bg-opacity-20" />
          <motion.img
            src={ASSETS.ui.ghostHand.path}
            alt="Ghost Hand"
            className="absolute -bottom-6 sm:-bottom-8 -right-6 sm:-right-8 w-12 h-12 sm:w-16 sm:h-16"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      )}
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={modalStyle}
        className="relative bg-surface p-3 sm:p-6 rounded-lg shadow-xl max-w-xs sm:max-w-sm text-center pointer-events-auto border border-border-color mobile-tutorial-modal"
      >
        <h3 className="text-lg sm:text-xl font-bold mb-1.5 sm:mb-2 text-text-primary">{tutorialDef.title}</h3>
        <p className="text-text-secondary mb-2 sm:mb-4 text-sm sm:text-base">{currentStepInfo.text}</p>
        <div className="text-xs sm:text-sm text-text-secondary mb-2 sm:mb-4">
          Step {currentTutorial.step + 1} / {tutorialDef.steps.length}
        </div>
        <div className="flex justify-center gap-2 sm:gap-4">
          {!isLastStep && (
            <button
              onClick={skipTutorial}
              className="bg-border-color text-text-secondary px-3 sm:px-4 py-2 rounded-lg hover:bg-text-secondary hover:text-white transition-colors text-sm"
            >
              Skip
            </button>
          )}
          {!isInteractive && (
            <button
              onClick={advanceStep}
              className="bg-primary-action text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-accent transition-colors text-sm"
            >
              {isLastStep ? "Got it!" : "Next"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default TutorialOverlay;
